// Pure computation of the message-permalink PATH the app actually parses —
// extracted out of message-link.ts so it's testable under plain-node vitest
// (message-link.ts pulls in workspaceState/authState from state/app.svelte.js,
// which can't be imported there — Svelte runes — same reason cybo-dm-peer-id.ts
// / channel-message-active.ts / channel-roster-refresh.ts were split out).
//
// Route contract: /workspace/<wsId>/channel/<channelId> for a channel
// message, /workspace/<wsId>/dm/<peerId> for a DM, with the target message
// carried via the `?m=<messageId>` query param — PR #1515's deep-link
// vocabulary, consumed by messageFocusState.focus() on both the
// channel/[channelId] and dm/[userId] routes. Mirrors activity-registry.ts's
// approval-card resolver (client) and approval-card.ts's
// buildApprovalDeepLinkUrl (server) — the SAME shape, not a third variant.
//
// NOT a `/message/<id>` path segment: no route exists under
// workspace/[id]/channel/[channelId]/ (or anywhere else) for it, so that
// shape 404s — adapter-static's fallback shell renders, the client router
// finds no match, and SvelteKit's own 404 replaces the page.
import { resolveShareOrigin } from "./page-share-url.js";
import { isMe, type ViewerIdentity } from "./session-scope.js";

/** The query param that names an open thread's root (read by thread-url-sync's deepLinkFocus). */
export const THREAD_PARAM = "thread";

export interface MessageLinkPathInput {
  messageId: string;
  channelId: string | null;
  fromId: string;
  toId?: string | null;
  parentId?: string | null;
  /** message.workspaceId ?? the active workspace id — already resolved by the caller. */
  workspaceId: string | undefined;
  /**
   * The viewer, to resolve the DM counterparty from fromId/toId. Pass the user
   * OBJECT (`authState.user`), not an id: a human holds TWO ids (session-scope.ts
   * `ViewerIdentity`) and only the object carries the second one. A bare id still
   * type-checks and degrades to the old single-id compare.
   */
  viewer: ViewerIdentity;
}

/** `?m=<id>`, plus `&thread=<root>` for a reply: it lives in its thread pane, not the timeline (deepLinkFocus). */
export function messageDeepLink(messageId: string, parentId?: string | null): string {
  const parent = parentId !== messageId ? parentId : undefined;
  return (
    `?m=${encodeURIComponent(messageId)}` +
    (parent ? `&${THREAD_PARAM}=${encodeURIComponent(parent)}` : "")
  );
}

export function resolveMessageLinkPath(input: MessageLinkPathInput): string | null {
  if (!input.workspaceId) return null;
  const deepLink = messageDeepLink(input.messageId, input.parentId);
  if (input.channelId) {
    return `/workspace/${input.workspaceId}/channel/${input.channelId}${deepLink}`;
  }
  // DM message: the peer is whichever party isn't the viewer — the SAME call as
  // app.svelte.ts's dmCounterparty(). Through `isMe`, never `fromId === myId`: my own
  // message arrives stamped with EITHER of my two ids (the transport picks), so a bare
  // compare called it someone else's and made "Copy link" a DM with MYSELF.
  if (!input.viewer) return null; // no "me" → "whichever party isn't me" has no answer
  const peerId = isMe(input.fromId, input.viewer) ? (input.toId ?? null) : input.fromId;
  if (!peerId) return null;
  return `/workspace/${input.workspaceId}/dm/${peerId}${deepLink}`;
}

// A thread ROOT's address: the root in its own timeline with its thread open beside it
// (`?m=X&thread=X`, deepLinkFocus). A cybo peer lives on the agent route, which reads
// `?m=` alone. No destination → undefined.
export function resolveThreadRootPath(
  input: Omit<MessageLinkPathInput, "parentId"> & { isCybo: (id: string) => boolean },
): string | undefined {
  if (!input.workspaceId) return undefined;
  const ws = `/workspace/${input.workspaceId}`;
  const id = encodeURIComponent(input.messageId);
  const open = `?m=${id}&${THREAD_PARAM}=${id}`;
  if (input.channelId) return `${ws}/channel/${input.channelId}${open}`;
  if (!input.viewer) return undefined;
  const peerId = isMe(input.fromId, input.viewer) ? input.toId : input.fromId;
  if (!peerId) return undefined;
  return input.isCybo(peerId) ? `${ws}/agent/${peerId}?m=${id}` : `${ws}/dm/${peerId}${open}`;
}

/**
 * Full shareable message URL: resolveShareOrigin() + resolveMessageLinkPath()
 * — mirrors page-share-url.ts's pagePath()/pageShareUrl() split. Never
 * `window.location.origin` verbatim: that's `cyborg://app` inside the
 * Electron desktop shell and meaningless inside the Tauri Android/iOS shells
 * too, so "Copy link" must go through resolveShareOrigin()'s real-web-origin-
 * else-canonical-app-origin rule instead of pasting an unusable URL.
 */
export function resolveMessageLinkUrl(input: MessageLinkPathInput): string | null {
  const path = resolveMessageLinkPath(input);
  if (!path) return null;
  return `${resolveShareOrigin()}${path}`;
}
